import { useState, useEffect, useRef } from 'react'
import { NavLink } from 'react-router-dom'
import { NAV_ITEMS, DEFAULT_MORE_SLUGS } from '@/lib/constants'
import { useAuth } from '@/lib/AuthContext'

const PORTFOLIO_MANAGER_MAIN_NAV = [
  { to: '/portfolio-manager/chat', label: 'Chat', icon: '💬', subtitle: 'AI assistant' },
  { to: '/portfolio-manager/constitution', label: 'Constitution', icon: '📜', subtitle: 'Rules & persona' },
  { to: '/portfolio-manager/prompts', label: 'Prompt Library', icon: '📋', subtitle: 'Reusable prompts' },
  { to: '/portfolio-manager/tmf-rankings', label: 'TMF Rankings', icon: '📊', subtitle: 'Monthly stock rankings' },
  { to: '/portfolio-manager/recommendations', label: 'Recommendations', icon: '💡', subtitle: 'Trade ideas' },
  { to: '/portfolio-manager/usage', label: 'Usage & Costs', icon: '📊', subtitle: 'AI spend tracking' },
]

const PORTFOLIO_MANAGER_MORE_NAV = [
  { to: '/portfolio-manager/history', label: 'History', icon: '📊', subtitle: 'Past recommendations' },
  { to: '/portfolio-manager/alerts', label: 'Alerts', icon: '🔔', subtitle: 'Active alerts' },
]

const STORAGE_KEY = 'tradingApp_navNames'
const MORE_ITEMS_KEY = 'tradingApp_moreItems'

function getCustomNames(): Record<string, string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

function saveCustomNames(names: Record<string, string>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(names))
}

function getMoreItems(): string[] {
  try {
    const stored = localStorage.getItem(MORE_ITEMS_KEY)
    return stored ? JSON.parse(stored) : DEFAULT_MORE_SLUGS
  } catch {
    return DEFAULT_MORE_SLUGS
  }
}

function saveMoreItems(items: string[]) {
  localStorage.setItem(MORE_ITEMS_KEY, JSON.stringify(items))
}

export function SideNav({ mobile = false, onNavigate }: { mobile?: boolean; onNavigate?: () => void } = {}) {
  const allNavItems = NAV_ITEMS
  const { appUser, logOut } = useAuth()

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('tradingApp_sideNavCollapsed') === 'true' } catch { return false }
  })
  const [journalExpanded, setJournalExpanded] = useState(true)
  const [pmExpanded, setPmExpanded] = useState(true)
  const [moreExpanded, setMoreExpanded] = useState(false)
  const [pmMoreExpanded, setPmMoreExpanded] = useState(false)
  const [moreItemSlugs, setMoreItemSlugs] = useState<string[]>(getMoreItems)
  const [customNames, setCustomNames] = useState<Record<string, string>>(getCustomNames)
  const [editingTo, setEditingTo] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const mainNavItems = allNavItems.filter(item => !moreItemSlugs.includes(item.to))
  const moreNavItems = allNavItems.filter(item => moreItemSlugs.includes(item.to))

  const moveToMore = (to: string) => {
    const updated = [...moreItemSlugs, to]
    setMoreItemSlugs(updated)
    saveMoreItems(updated)
  }

  const moveToMain = (to: string) => {
    const updated = moreItemSlugs.filter(s => s !== to)
    setMoreItemSlugs(updated)
    saveMoreItems(updated)
  }

  useEffect(() => {
    if (editingTo && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editingTo])

  const handleDoubleClick = (to: string, currentLabel: string) => {
    setEditingTo(to)
    setEditValue(customNames[to] || currentLabel)
  }

  const handleSave = () => {
    if (!editingTo) return
    const trimmed = editValue.trim()
    const updated = { ...customNames }
    if (trimmed && trimmed !== '') {
      updated[editingTo] = trimmed
    } else {
      delete updated[editingTo]
    }
    setCustomNames(updated)
    saveCustomNames(updated)
    setEditingTo(null)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      setEditingTo(null)
    }
  }

  const renderNavItem = (item: { to: string; label: string; icon: string; subtitle: string }, isInMore: boolean) => {
    const displayLabel = customNames[item.to] || item.label

    return (
      <div key={item.to} className="relative group/nav">
        <NavLink
          to={item.to}
          end={item.to === '/'}
          className={({ isActive }) =>
            `block w-full px-4 py-5 border-b border-[var(--color-border)] transition-all duration-150 ${
              isActive
                ? 'bg-[var(--color-accent)]/10 border-l-4 border-l-[var(--color-accent)]'
                : 'bg-[var(--color-bg-secondary)] hover:bg-[var(--color-bg-tertiary)] border-l-4 border-l-transparent'
            }`
          }
        >
          {({ isActive }) => (
            <div className="flex items-center gap-3">
              <span className="text-2xl leading-none">{item.icon}</span>
              <div className="flex flex-col min-w-0 flex-1">
                {editingTo === item.to ? (
                  <input
                    ref={inputRef}
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => e.preventDefault()}
                    className="text-sm font-semibold bg-white border border-[var(--color-accent)] rounded px-1 py-0.5 outline-none w-full"
                  />
                ) : (
                  <span
                    className={`text-sm font-semibold truncate ${
                      isActive ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-primary)]'
                    }`}
                    onDoubleClick={(e) => {
                      e.preventDefault()
                      handleDoubleClick(item.to, item.label)
                    }}
                  >
                    {displayLabel}
                  </span>
                )}
                <span className={`text-[11px] mt-0.5 truncate ${
                  isActive ? 'text-[var(--color-accent)]/70' : 'text-[var(--color-text-muted)]'
                }`}>
                  {item.subtitle}
                </span>
              </div>
            </div>
          )}
        </NavLink>
        {/* Move button — appears on hover */}
        <button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (isInMore) moveToMain(item.to)
            else moveToMore(item.to)
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover/nav:opacity-100 transition-opacity px-1.5 py-0.5 text-[10px] rounded bg-[var(--color-bg-tertiary)] border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-accent)]"
          title={isInMore ? 'Move to main nav' : 'Move to More'}
        >
          {isInMore ? '↑' : '↓'}
        </button>
      </div>
    )
  }

  const toggleCollapsed = () => {
    const next = !collapsed
    setCollapsed(next)
    localStorage.setItem('tradingApp_sideNavCollapsed', String(next))
  }

  return (
    <nav
      className={
        mobile
          ? 'h-full w-72 flex flex-col bg-[var(--color-bg-secondary)] overflow-y-auto'
          : `h-screen sticky top-0 flex flex-col bg-[var(--color-bg-secondary)] overflow-y-auto transition-all duration-200 ${
              collapsed ? 'w-14' : 'w-56'
            }`
      }
      aria-label="Main navigation"
      onClickCapture={mobile ? (e) => {
        // Close the drawer when a navigation link is tapped (not when clicking toggles).
        const target = e.target as HTMLElement
        if (target.closest('a')) onNavigate?.()
      } : undefined}
    >
      {/* Collapse toggle — desktop only (drawer always shows the full list) */}
      {!mobile && (
        <button
          onClick={toggleCollapsed}
          className="sticky top-0 z-10 flex items-center justify-center h-10 bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)] transition-colors shrink-0"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <span className="text-sm text-[var(--color-text-muted)]">{collapsed ? '▶' : '◀'}</span>
        </button>
      )}

      {collapsed && !mobile ? (
        /* Collapsed: icons only */
        <div className="flex-1 flex flex-col items-center py-2 gap-1 overflow-y-auto">
          {allNavItems.slice(0, 6).map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                  isActive ? 'bg-[var(--color-accent)]/10 text-[var(--color-accent)]' : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)]'
                }`
              }
              title={item.label}
            >
              <span className="text-lg">{item.icon}</span>
            </NavLink>
          ))}
          <div className="my-1 w-6 border-t border-[var(--color-border)]" />
          <NavLink
            to="/portfolio-manager"
            className={({ isActive }) =>
              `flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                isActive ? 'bg-[var(--color-accent)]/10 text-[var(--color-accent)]' : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)]'
              }`
            }
            title="AI Portfolio Manager"
          >
            <span className="text-lg">🎯</span>
          </NavLink>
        </div>
      ) : (
        /* Expanded: full sidebar */
        <>
      <div className="px-4 py-5 cursor-pointer" onClick={() => setJournalExpanded(!journalExpanded)}>
        <span className="text-base font-bold text-[var(--color-text-primary)]">Trading Journal {journalExpanded ? '▼' : '▶'}</span>
      </div>

      <div className="flex-1 px-2 pb-2 space-y-0">
        {journalExpanded && mainNavItems.map(item => renderNavItem(item, false))}

        {/* More section */}
        {journalExpanded && (
          <>
            <button
              onClick={() => setMoreExpanded(!moreExpanded)}
              className="block w-full px-4 py-4 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)] hover:bg-[var(--color-bg-tertiary)] border-l-4 border-l-transparent text-left transition-all duration-150"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl leading-none">📂</span>
                <span className="text-sm font-semibold text-[var(--color-text-muted)]">
                  More {moreExpanded ? '▼' : '▶'} ({moreNavItems.length})
                </span>
              </div>
            </button>

            {moreExpanded && moreNavItems.map(item => renderNavItem(item, true))}
          </>
        )}

        {/* Separator */}
        <div className="my-3 mx-4 border-t border-[var(--color-border)]" />

        {/* Portfolio Manager Section */}
        <div className="px-4 py-3 cursor-pointer" onClick={() => setPmExpanded(!pmExpanded)}>
          <span className="text-base font-bold text-[var(--color-text-primary)]">AI Portfolio Manager {pmExpanded ? '▼' : '▶'}</span>
        </div>

        {pmExpanded && PORTFOLIO_MANAGER_MAIN_NAV.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `block w-full px-4 py-5 border-b border-[var(--color-border)] transition-all duration-150 ${
                isActive
                  ? 'bg-[var(--color-accent)]/10 border-l-4 border-l-[var(--color-accent)]'
                  : 'bg-[var(--color-bg-secondary)] hover:bg-[var(--color-bg-tertiary)] border-l-4 border-l-transparent'
              }`
            }
          >
            {({ isActive }) => (
              <div className="flex items-center gap-3">
                <span className="text-2xl leading-none">{item.icon}</span>
                <div className="flex flex-col min-w-0 flex-1">
                  <span
                    className={`text-sm font-semibold truncate ${
                      isActive ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-primary)]'
                    }`}
                  >
                    {item.label}
                  </span>
                  <span className={`text-[11px] mt-0.5 truncate ${
                    isActive ? 'text-[var(--color-accent)]/70' : 'text-[var(--color-text-muted)]'
                  }`}>
                    {item.subtitle}
                  </span>
                </div>
              </div>
            )}
          </NavLink>
        ))}

        {/* PM More section */}
        {pmExpanded && (
          <>
            <button
              onClick={() => setPmMoreExpanded(!pmMoreExpanded)}
              className="block w-full px-4 py-4 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)] hover:bg-[var(--color-bg-tertiary)] border-l-4 border-l-transparent text-left transition-all duration-150"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl leading-none">📂</span>
                <span className="text-sm font-semibold text-[var(--color-text-muted)]">
                  More {pmMoreExpanded ? '▼' : '▶'} ({PORTFOLIO_MANAGER_MORE_NAV.length})
                </span>
              </div>
            </button>

            {pmMoreExpanded && PORTFOLIO_MANAGER_MORE_NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `block w-full px-4 py-5 border-b border-[var(--color-border)] transition-all duration-150 ${
                    isActive
                      ? 'bg-[var(--color-accent)]/10 border-l-4 border-l-[var(--color-accent)]'
                      : 'bg-[var(--color-bg-secondary)] hover:bg-[var(--color-bg-tertiary)] border-l-4 border-l-transparent'
                  }`
                }
              >
                {({ isActive }) => (
                  <div className="flex items-center gap-3">
                    <span className="text-2xl leading-none">{item.icon}</span>
                    <div className="flex flex-col min-w-0 flex-1">
                      <span
                        className={`text-sm font-semibold truncate ${
                          isActive ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-primary)]'
                        }`}
                      >
                        {item.label}
                      </span>
                      <span className={`text-[11px] mt-0.5 truncate ${
                        isActive ? 'text-[var(--color-accent)]/70' : 'text-[var(--color-text-muted)]'
                      }`}>
                        {item.subtitle}
                      </span>
                    </div>
                  </div>
                )}
              </NavLink>
            ))}
          </>
        )}
      </div>

      {/* Account footer — signed-in email, admin link, sign out */}
      <div className="mt-auto border-t border-[var(--color-border)] px-4 py-3 space-y-2 shrink-0">
        {appUser?.role === 'admin' && (
          <NavLink
            to="/admin/users"
            className={({ isActive }) =>
              `block text-sm font-medium ${isActive ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'}`
            }
          >
            👤 User Management
          </NavLink>
        )}
        <div className="text-[11px] text-[var(--color-text-muted)] truncate" title={appUser?.email || ''}>
          {appUser?.email}
        </div>
        <button
          onClick={logOut}
          className="w-full px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
        >
          Sign out
        </button>
      </div>
        </>
      )}
    </nav>
  )
}
